import { FC, createContext, useCallback, useContext, useState } from 'react';
import omitBy from 'lodash/fp/omitBy';
import noop from 'lodash/noop';

import {
  CHANGE_DATE_REQUEST_STAGES as stages,
  MODAL_HEADER_ON_CHANGE_DATE_REQUEST_FAILURE,
  MODAL_BODY_ON_CHANGE_DATE_REQUEST_FAILURE,
} from 'config/constants';
import useDetailsBooking from 'hooks/useDetailsBooking';
import { modalCloseButton as trackModalCloseButton } from 'lib/gaTracking/crossPageEvents';
import { ChangeDatesRequestFlowContextValue, FormValue } from './types';
import { useNavigateTo } from 'lib/hooks';
import useGlobalModal from 'hooks/useGlobalModal';
import { SelfServiceAPI } from 'apis';
import { useAsyncFn } from 'react-use';

const omitEmptyValues = omitBy((value) => !value);

const initialFormValue = {
  checkIn: '',
  checkOut: '',
  phoneNumber: '',
  email: '',
};

const ChangeDatesRequestFlowContext = createContext<ChangeDatesRequestFlowContextValue>({
  goToBookingDetails: noop,
  booking: undefined,
  stage: stages.EDIT,
  initialValues: initialFormValue,
  originalValues: initialFormValue,
  onSubmitDraft: noop,
  values: initialFormValue,
  backToForm: noop,
  createDateAmendmentRequest: noop,
  loading: false,
  caseNumber: undefined,
});

const ChangeDatesRequestFlowProvider: FC = ({ children }) => {
  const { showModal } = useGlobalModal();
  const navigateTo = useNavigateTo();
  const { booking } = useDetailsBooking();
  const [stage, setStage] = useState(stages.EDIT);
  const [values, setValues] = useState(initialFormValue);
  const onSubmitDraft = useCallback(
    (val) => {
      setValues(val);
      setStage(stages.REVIEW);
    },
    [setValues, setStage],
  );
  const backToForm = useCallback(() => setStage(stages.EDIT), [setStage]);
  const goToBookingDetails = useCallback(() => navigateTo.booking('push', booking.id), [booking.id, navigateTo]);

  const [{ loading, value }, createDateAmendmentRequest] = useAsyncFn(async (formValue: FormValue) => {
    try {
      const response = await SelfServiceAPI.createDateAmendmentRequest({
        bookingId: booking.id,
        checkIn: formValue.checkIn,
        checkOut: formValue.checkOut,
        phoneNumber: formValue.phoneNumber,
        email: formValue.email,
      });
      setStage(stages.CONFIRMED);
      return response;
    } catch {
      showModal({
        variant: 'error',
        header: MODAL_HEADER_ON_CHANGE_DATE_REQUEST_FAILURE,
        body: MODAL_BODY_ON_CHANGE_DATE_REQUEST_FAILURE,
        onClose: trackModalCloseButton,
      });
    }
  });

  const originalValues = {
    checkIn: booking.reservation.checkIn,
    checkOut: booking.reservation.checkOut,
    phoneNumber: booking.travelArranger.phone ?? '',
    email: booking.travelArranger.email,
  };

  const onCreateDateAmendmentRequest = (formValue: FormValue) => {
    setStage(stages.SUBMITTING);
    createDateAmendmentRequest(formValue);
  };

  const contextValue = {
    goToBookingDetails,
    booking,
    stage,
    initialValues: { ...originalValues, ...omitEmptyValues(values) },
    originalValues,
    onSubmitDraft,
    values,
    backToForm,
    createDateAmendmentRequest: onCreateDateAmendmentRequest,
    loading,
    caseNumber: value?.caseNumber,
  };

  return <ChangeDatesRequestFlowContext.Provider value={contextValue}>{children}</ChangeDatesRequestFlowContext.Provider>;
};

export default ChangeDatesRequestFlowProvider;
export const useChangeDatesRequestFlow = () => useContext(ChangeDatesRequestFlowContext);
